import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Scale, Dumbbell, TrendingUp, Camera, Droplet, LogOut, Utensils, Newspaper, User, Users, Home } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuth } from '../../contexts/AuthContext'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useProfile } from '../../hooks/useProfile'
import PhoenixLogo from '../../components/PhoenixLogo'
import PullToRefresh from '../../components/PullToRefresh'
import WeeklyInsightsCard from '../../components/WeeklyInsightsCard'
import { PageLoader } from '../../components/PageLoader'
import { triggerUpdate } from '../../serviceWorkerRegistration'
import { LoginPage } from './LoginPage'
import OnboardingWizard from './OnboardingWizard'
import { WeightTab } from './WeightTab'
import { MealsTab } from './MealsTab'
import { MeasurementsTab } from './MeasurementsTab'
import { ProgressTab } from './ProgressTab'
import { PhotosTab } from './PhotosTab'
import TodayTab from './TodayTab'
import { ExportButton } from './components/ExportButton'
import { UnitsToggle } from './components/UnitsToggle'

// 'workouts' and 'news' aren't real internal tabs — clicking them
// navigates to /workout and /phoenix/news respectively. Measurements
// + photos are still reachable from the profile dropdown but don't
// get a bottom-nav slot — owner asked to swap photos out for news
// since they don't open photos daily.
// Today replaces Weight as the first slot — the app's new default
// landing surface (a dashboard rather than a data-entry pane). Weight
// is still reachable via the Today check-in pill, ProfileSheet, and
// the existing state-driven tab system (so old deep-links with
// `state.tab='weight'` still work). Progress moved off the primary
// bar into the Today module grid for the same reason — it's a glance
// surface, not a daily-action one. Workouts kept on the primary bar
// because it navigates to its own /workout route, not a sub-tab.
export const TABS = [
  { key: 'today', icon: Home },
  { key: 'meals', icon: Utensils },
  { key: 'workouts', icon: Dumbbell },
  // Social over News in the primary bar — owner's main reason to
  // open the app daily. /news still reachable via ProfileSheet.
  { key: 'social', icon: Users },
  { key: 'weight', icon: Scale },
  // Profile tab — taps open a bottom sheet (see ProfileSheet) rather
  // than navigating. Replaces the old top-right floating dropdown.
  { key: 'profile', icon: User },
]


export const PhoenixTracker = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading: authLoading, signOut } = useAuth()
  const { profile, loading: profileLoading, completeOnboarding } = useProfile()
  const confirm = useConfirm()
  const isMobile = useIsMobile()
  // Initial tab can be forwarded via `navigate('/', { state: { tab: ... } })`
  // from the workout page's bottom nav — otherwise default to weight.
  const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'today')

  // Workouts isn't an internal Phoenix tab — it navigates to the
  // standalone /workout page. Everything else just swaps panes.
  const handleSelectTab = useCallback(
    (key) => {
      if (key === 'workouts') {
        navigate('/workout')
        return
      }
      if (key === 'news') {
        navigate('/news')
        return
      }
      setActiveTab(key)
    },
    [navigate]
  )
  const [editingGoals, setEditingGoals] = useState(false)
  // Notification banner + tip card moved into TodayTab — they only
  // surface on the new home dashboard, not on the data-entry tabs.

  // Open the wizard in editing mode when ProfileDropdown or
  // ProgressTab dispatches the event.
  useEffect(() => {
    const onEdit = () => setEditingGoals(true)
    window.addEventListener('phoenix:edit-profile', onEdit)
    return () => window.removeEventListener('phoenix:edit-profile', onEdit)
  }, [])

  // Profile dropdown's "Measurements" shortcut — measurements got
  // booted off the bottom nav to make room for Workouts, so we open
  // the tab via an event instead.
  useEffect(() => {
    const onOpen = () => setActiveTab('measurements')
    window.addEventListener('phoenix:open-measurements', onOpen)
    return () => window.removeEventListener('phoenix:open-measurements', onOpen)
  }, [])

  // Same pattern for Photos — bumped off the bottom nav so News
  // could take its slot. Profile dropdown fires this event when the
  // user wants to open the photos pane.
  useEffect(() => {
    const onOpen = () => setActiveTab('photos')
    window.addEventListener('phoenix:open-photos', onOpen)
    return () => window.removeEventListener('phoenix:open-photos', onOpen)
  }, [])

  // "Go home" — fired by the breadcrumb's home icon. Only the edit-goals
  // wizard is still state-driven; Phases/Settings live at their own
  // routes now and react-router handles their back navigation.
  useEffect(() => {
    const goHome = () => {
      setEditingGoals(false)
    }
    window.addEventListener('phoenix:go-home', goHome)
    return () => window.removeEventListener('phoenix:go-home', goHome)
  }, [])

  // Broadcast the active tab so the global MobileBreadcrumb can render
  // it as a crumb. The URL stays at /phoenix for all six tabs, so we
  // can't derive this from the route alone. Fires on every change
  // Sync activeTab when a different tab is requested via the URL while
  // Phoenix is already mounted — the hoisted BottomTabBar calls
  // `navigate('/', { state: { tab } })` on every selection, but
  // the useState initializer above only runs once on first mount, so
  // re-navigates would otherwise be silent.
  useEffect(() => {
    const requested = location.state?.tab
    if (!requested) return
    setActiveTab((prev) => (prev === requested ? prev : requested))
  }, [location.state?.tab, location.key])

  // including the initial mount.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('phoenix:tab', { detail: activeTab }))
  }, [activeTab])

  const handleRefresh = useCallback(async () => {
    triggerUpdate()
    // Hand each `usePhoenixRefresh` listener an array to push its
    // refetch promise onto, so we can actually wait for the data to
    // come back instead of dismissing the spinner after a guess.
    const pending = []
    window.dispatchEvent(
      new CustomEvent('phoenix:refresh', { detail: { await: pending } })
    )
    // Floor the spinner at one full spin (Tailwind's animate-spin is
    // a 1s rotation). On a fast cache hit Supabase comes back in
    // ~100ms, well short of that — without the floor the loader cuts
    // mid-rotation and feels like a glitch.
    await Promise.all([
      Promise.allSettled(pending),
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ])
  }, [])

  const handleSignOut = async () => {
    const ok = await confirm({
      title: t('phoenixTracker.auth.confirmSignOut'),
      description: t('phoenixTracker.auth.confirmSignOutDesc'),
      confirmLabel: t('phoenixTracker.auth.signOut'),
      variant: 'destructive',
      icon: LogOut,
    })
    if (ok) await signOut()
  }

  if (authLoading) return <PageLoader />

  if (!user) {
    return <LoginPage />
  }

  // Wait for profile fetch to resolve before deciding onboarding state
  if (profileLoading) return <PageLoader />

  // First-time user (no profile row) or existing user without onboarded_at — show wizard
  if (!profile || !profile.onboarded_at) {
    return <OnboardingWizard profile={profile} onComplete={completeOnboarding} />
  }

  // Editing the profile/goals from anywhere in the app (dispatches `phoenix:edit-profile`)
  if (editingGoals) {
    return (
      <OnboardingWizard
        profile={profile}
        editing
        onComplete={async (values) => {
          const ok = await completeOnboarding(values)
          if (ok) setEditingGoals(false)
          return ok
        }}
      />
    )
  }

  return (
    <>
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-6 sm:space-y-8 content-fade">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3">
          <PhoenixLogo className="w-10 h-10 sm:w-12 sm:h-12 shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-3xl font-bold text-foreground truncate">
              {t('phoenixTracker.title')}
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">
              {t('phoenixTracker.subtitle')}
            </p>
          </div>
          <UnitsToggle />
          <ExportButton />
          {/* Sign-out only shown on desktop — mobile has it in the profile dropdown */}
          {!isMobile && (
            <button
              onClick={handleSignOut}
              className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-500 transition-colors"
              aria-label={t('phoenixTracker.auth.signOut')}
              title={t('phoenixTracker.auth.signOut')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Weekly AI insights — sits above the tab bar so the
            summary is the first thing the user sees on opening the
            dashboard. Hides itself when no row exists yet OR shows
            an empty-state CTA the user can tap to generate one
            on demand. */}
        <WeeklyInsightsCard />

        {/* Tab Bar (desktop only — mobile uses BottomTabBar) */}
        <div className="hidden lg:flex gap-1 p-1 bg-muted rounded-lg">
          {TABS.map(({ key, icon: Icon }) => (
            <button
              key={key}
              onClick={() => handleSelectTab(key)}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-sm font-medium transition-all duration-200',
                activeTab === key
                  ? 'bg-app-primary-500 text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{t(`phoenixTracker.tabs.${key}`)}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'today' && <TodayTab onSelectTab={handleSelectTab} />}
        {activeTab === 'weight' && <WeightTab />}
        {activeTab === 'meals' && <MealsTab />}
        {activeTab === 'measurements' && <MeasurementsTab />}
        {activeTab === 'photos' && <PhotosTab />}
        {activeTab === 'progress' && <ProgressTab />}
      </div>
    </PullToRefresh>
    {/* BottomTabBar is hoisted to App-level so it persists across
        navigation between /phoenix and /workout. Phoenix dispatches a
        `phoenix:tab` window event on activeTab change and the hoisted
        bar listens. */}
    </>
  )
}
